import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
    Table,
    Button,
    Flex,
    Breadcrumb,
    Form,
    Popconfirm,
    Space,
    Pagination,
    Switch,
    TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import EmployeeModal from './EmployeeModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import type { ColumnType, SortOrder } from 'antd/es/table/interface';


const useStyle = createStyles(({ css }) => ({
    customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Employee {
    employee_id: number;
    employee_fname: string;
    employee_lname: string;
    employee_mobile: string;
    employee_designation: string;
    employee_department: number;
    employee_type: string;
    employee_salary: number;
    employee_status: number;
    employee_address: string;
    department_name: string;
    designation_name: string;
}

const Employees: React.FC = () => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState<Employee | null>(null);
    const { styles } = useStyle();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const currentModule = useCurrentModule();

    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});


    const cancel: PopconfirmProps['onCancel'] = () => showError('Cancelled');

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const fetchEmployees = async () => {
        try {
            const data = await apiRequest<Employee[]>({
                method: 'get',
                url: '/employees',
            });
            setEmployees(data);
        } catch (error) {
            showError('Failed to fetch employees');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    const handleEditClick = (record: Employee) => {
        setIsEditMode(true);
        setInitialValues(record);
        setIsModalVisible(true);
    };

    const handleCreate = () => fetchEmployees();

    const showCreateModal = () => {
        setIsEditMode(false);
        setInitialValues(null);
        setIsModalVisible(true);
    };

    const hideModal = () => {
        setIsModalVisible(false);
        setIsEditMode(false);
        setInitialValues(null);
    };

    const handleDelete = async (employee_id: number) => {
        try {
            await apiRequest({
                method: 'delete',
                url: `/delete-employee/${employee_id}`,
            });
            showSuccess('Employee deleted');
            fetchEmployees();
        } catch {
            showError('Failed to delete employee');
        }
    };

    const handleStatusToggle = async (employeeId: number) => {
        setEmployees((prev) =>
            prev.map((item) =>
                item.employee_id === employeeId
                    ? { ...item, employee_status: item.employee_status === 1 ? 0 : 1 }
                    : item
            )
        );

        try {
            await apiRequest({
                method: 'put',
                url: `/update-status-employee/${employeeId}`,
            });
            showSuccess('Employee status updated');
        } catch (error) {
            showError('Failed to update status');
            setEmployees((prev) =>
                prev.map((item) =>
                    item.employee_id === employeeId
                        ? { ...item, employee_status: item.employee_status === 1 ? 0 : 1 }
                        : item
                )
            );
        }
    };

    const handleTableChange: TableProps<Employee>['onChange'] = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };



    const createTextColumn = <T extends Record<string, any>>(
        title: string,
        dataIndex: keyof T,
        data: T[],
        filteredInfo: Record<string, any>,
        sortedInfo: any,
        width?: number
    ): ColumnType<T> => ({
        title,
        dataIndex: dataIndex as string | number, // ✅ cast to allowed type
        key: dataIndex as string,
        width,
        filters: Array.from(
            new Set(
                data
                    .map((item) => item[dataIndex])
                    .filter((v) => typeof v === 'string' && v.length > 0) as string[]
            )
        ).map((value) => ({ text: value, value })),

        filteredValue: filteredInfo[dataIndex as string] ?? null,
        onFilter: (value: any, record: T) => {
            const recordValue = record[dataIndex];
            if (!recordValue) return false;
            return recordValue.toString().toLowerCase().includes(value.toString().toLowerCase());
        },
        sorter: (a: T, b: T) => {
            const aVal = a[dataIndex]?.toString() ?? '';
            const bVal = b[dataIndex]?.toString() ?? '';
            return aVal.localeCompare(bVal);
        },
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
        sortDirections: ['ascend', 'descend'] as SortOrder[],
        ellipsis: true,
        render: (text: string | number) => (
            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {text}
            </div>
        ),
    });



    const columns: TableColumnsType<Employee> = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => `${record.employee_fname} ${record.employee_lname}`,
            sorter: (a, b) =>
                `${a.employee_fname} ${a.employee_lname}`.localeCompare(
                    `${b.employee_fname} ${b.employee_lname}`
                ),
        },
        createTextColumn('Mobile', 'employee_mobile', employees, filteredInfo, sortedInfo, 150),
        createTextColumn('Designation', 'designation_name', employees, filteredInfo, sortedInfo, 200),
        createTextColumn('Department', 'department_name', employees, filteredInfo, sortedInfo, 200),
        createTextColumn('Type', 'employee_type', employees, filteredInfo, sortedInfo, 150),
        {
            title: 'Salary',
            dataIndex: 'employee_salary',
            key: 'employee_salary',
            render: (val) => `₹ ${val}`,
        },
        createTextColumn('address', 'employee_address', employees, filteredInfo, sortedInfo, 250),
    ];


    // Action column
    if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
        columns.push({
            title: 'Action',
            width: 220,
            render: (_: any, record: Employee) => (
                <Space size="middle">
                    {currentModule?.right_edit === 'Y' && (
                        <>
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                                checked={!!record.employee_status}
                                onChange={() => handleStatusToggle(record.employee_id)}
                            />
                            <Button onClick={() => handleEditClick(record)}>
                                <EditOutlined />
                            </Button>
                        </>
                    )}
                    {currentModule?.right_delete === 'Y' && (
                        <Popconfirm
                            title="Delete this employee?"
                            onConfirm={() => handleDelete(record.employee_id)}
                            onCancel={cancel}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger>
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        });
    }

    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: 16,
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb>
                    <Breadcrumb.Item href="/">
                        <HomeOutlined />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>View Employees</Breadcrumb.Item>
                </Breadcrumb>

                {currentModule?.right_add === 'Y' && (
                    <Button type="primary" onClick={showCreateModal}>
                        Add Employee
                    </Button>
                )}
            </Space>

            <div
                style={{
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Table<Employee>
                    className={`${styles.customTable}`}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="employee_id"
                    dataSource={employees.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    bordered
                    pagination={false}
                    locale={{ emptyText: 'No employee found' }}
                    footer={() => (
                        <Flex justify="space-between">
                            <span>
                                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                                {Math.min(pagination.current * pagination.pageSize, employees.length)} of{' '}
                                {employees.length}
                            </span>
                            <Pagination
                                {...pagination}
                                total={employees.length}
                                showSizeChanger
                                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
                            />
                        </Flex>
                    )}
                />
            </div>

            <EmployeeModal
                visible={isModalVisible}
                onCancel={hideModal}
                onCreate={handleCreate}
                isEditMode={isEditMode}
                initialValues={initialValues}
            />
        </>
    );
};

export default Employees;
